import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, MapPin, ArrowRight, Flame, Crown } from "lucide-react";

const MEDALS = ["", "🥇", "🥈", "🥉"];
const COLORS = [
  "from-[#F2C053] to-[#D4A63D]", // gold
  "from-[#E89BB8] to-[#D4748E]", // pink
  "from-[#9C8FE0] to-[#7163C0]", // violet
  "from-[#5FA0E8] to-[#3F7EC8]", // blue
  "from-[#3BA76D] to-[#2D8557]", // green
];

export const LeaderboardJudete = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/petitions/leaderboard/counties", { params: { limit: 5 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || (data.counties || []).length === 0) return null;

  const total = data.total_signatures_with_county || 0;
  const max = data.counties[0]?.signatures || 1;

  return (
    <section
      data-testid="leaderboard-judete"
      className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-10"
    >
      <div className="dense-card relative overflow-hidden p-6 md:p-8">
        <div
          className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-15 pointer-events-none blur-3xl"
          style={{ background: "radial-gradient(circle, #D4A63D 0%, transparent 70%)" }}
        />

        <div className="relative grid lg:grid-cols-12 gap-6 items-start">
          {/* HEADER */}
          <div className="lg:col-span-4">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.25em] uppercase text-[#D4A63D] bg-[#D4A63D]/10 border border-[#D4A63D]/30 px-2.5 py-1 rounded-sm">
              <Trophy className="w-3 h-3" /> Mobilizare națională
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-[#F8F8F4] leading-tight mt-3">
              <span className="gold-text">Top 5 județe</span> care fac diferența
            </h2>
            <p className="mt-3 text-[13px] text-[#A0AABF] leading-relaxed">
              Județele cu cele mai multe semnături pentru petițiile active. Fii ambasadorul județului tău - fiecare voce contează.
            </p>
            <div className="mt-5 flex items-center gap-4 pt-4 border-t border-white/5">
              <div>
                <div className="font-display text-2xl gold-text leading-none">{total.toLocaleString("ro-RO")}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6C7A9C] mt-1">Semnături cu județ</div>
              </div>
              <div>
                <div className="font-display text-2xl text-[#E89BB8] leading-none">{data.counties.length}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6C7A9C] mt-1">Județe active</div>
              </div>
            </div>
            <Link
              to="/petitii"
              data-testid="leaderboard-cta"
              className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0]"
            >
              VEZI TOATE PETIȚIILE <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* LEADERBOARD ROWS */}
          <div className="lg:col-span-8 space-y-2.5">
            {data.counties.map((c, i) => {
              const pct = (c.signatures / max) * 100;
              const isLeader = i === 0;
              return (
                <div
                  key={c.county}
                  data-testid={`leaderboard-row-${i}`}
                  className={`relative flex items-center gap-3 rounded-sm border px-4 py-3 transition-all ${
                    isLeader
                      ? "border-[#D4A63D]/40 bg-[#D4A63D]/5"
                      : "border-white/10 bg-[#0A1020]/40 hover:border-white/20"
                  }`}
                >
                  <div className={`shrink-0 w-9 h-9 rounded-sm grid place-items-center font-display text-base ${
                    i < 3 ? "bg-[#0A1020] text-[#F8F8F4]" : "bg-white/5 text-[#A0AABF]"
                  }`}>
                    {i === 0 ? <Crown className="w-4 h-4 text-[#D4A63D]" /> : (
                      <span className="text-[13px]">{MEDALS[i + 1] || `#${i + 1}`}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-[#F8F8F4] truncate">
                        <MapPin className="w-3.5 h-3.5 text-[#6C7A9C]" />
                        {c.county}
                        {isLeader && <Flame className="w-3.5 h-3.5 text-[#E89BB8]" />}
                      </div>
                      <div className="text-[12px] text-[#A0AABF] font-mono whitespace-nowrap">
                        <strong className="text-[#D4A63D]">{c.signatures.toLocaleString("ro-RO")}</strong> semnături
                      </div>
                    </div>
                    <div className="h-1.5 bg-[#0A1020] rounded-full overflow-hidden border border-white/5">
                      <div
                        className={`h-full bg-gradient-to-r ${COLORS[i % COLORS.length]} transition-all duration-700 ease-out`}
                        style={{ width: `${Math.max(8, pct)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeaderboardJudete;
